import { forwardRef, type ReactNode } from 'react';
import MuiIconButton, { type IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';

/**
 * IconButton
 * Classification: mui-wrapper (themed MUI IconButton + a11y + tooltip + loading)
 * MUI base: https://mui.com/material-ui/react-button/#icon-button
 *
 * A compact, icon-only action. Unlike raw MUI, this enforces an accessible
 * `label` (rendered as `aria-label`), adds an optional `tooltip`, semantic
 * `variant` colors, and a `loading` spinner.
 */
export interface IconButtonProps
  extends Omit<MuiIconButtonProps, 'children' | 'color'> {
  /** The icon to render (e.g. a `@mui/icons-material` element). */
  icon: ReactNode;
  /** Accessible name for the button. Required for a11y since there is no text. */
  label: string;
  /** Visual emphasis. Maps to a themed color. */
  variant?: 'default' | 'primary' | 'danger';
  /** Optional tooltip. Defaults to `label` when omitted and `showTooltip` is set. */
  tooltip?: ReactNode;
  /** Placement for the tooltip when `tooltip` is provided. */
  tooltipPlacement?: TooltipProps['placement'];
  /** Show a spinner and disable interaction. */
  loading?: boolean;
}

const variantColor: Record<
  NonNullable<IconButtonProps['variant']>,
  MuiIconButtonProps['color']
> = {
  default: 'default',
  primary: 'primary',
  danger: 'error',
};

const spinnerSize: Record<NonNullable<MuiIconButtonProps['size']>, number> = {
  small: 16,
  medium: 20,
  large: 24,
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      variant = 'default',
      tooltip,
      tooltipPlacement,
      loading = false,
      disabled,
      size = 'medium',
      ...rest
    },
    ref,
  ) {
    const button = (
      <MuiIconButton
        ref={ref}
        aria-label={label}
        aria-busy={loading || undefined}
        color={variantColor[variant]}
        size={size}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <CircularProgress size={spinnerSize[size]} color="inherit" thickness={5} />
        ) : (
          icon
        )}
      </MuiIconButton>
    );

    if (!tooltip) return button;

    // Wrap in a span so the tooltip still shows when the button is disabled.
    return (
      <Tooltip title={tooltip} placement={tooltipPlacement}>
        <span style={{ display: 'inline-flex' }}>{button}</span>
      </Tooltip>
    );
  },
);
